import React, { useMemo, useRef } from 'react';

import { PATH_CONFIG } from '../../../../../config/static/path.config';
import { useSwitch } from '../../../../hooks/useSwitch';
import { PdfPreviewButton } from './PdfPreviewButton';
import { ExternalLinkWrapper } from '@ael/shared-components-library';
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { SpecialZoomLevel, Viewer, Worker } from '@react-pdf-viewer/core';
import '@react-pdf-viewer/core/lib/styles/index.css';
import { FullScreenPlugin, fullScreenPlugin } from '@react-pdf-viewer/full-screen';
import '@react-pdf-viewer/full-screen/lib/styles/index.css';
import {
  RenderCurrentScaleProps,
  RenderZoomInProps,
  RenderZoomOutProps,
  ZoomPlugin,
  zoomPlugin,
} from '@react-pdf-viewer/zoom';
import '@react-pdf-viewer/zoom/lib/styles/index.css';
import classNames from 'classnames';

import './pdf-preview.scss';

type PDFWidgetProps = {
  src: string | undefined;
  allowDownload: boolean;
  showToolbar?: boolean;
  inSmallWindow: boolean;
};

// https://github.com/react-pdf-viewer/react-pdf-viewer/issues/832
// License key: 43775AB7-8CC44AC8-977A2C64-44C2B3B5

const createPlugins = (fullScreenPluginInstance: FullScreenPlugin, zoomPlugin: ZoomPlugin, allowDownload: boolean) => {
  if (!allowDownload) {
    return [fullScreenPluginInstance, zoomPlugin];
  }

  return [fullScreenPluginInstance, zoomPlugin];
};

export const PDFPreview: React.FC<PDFWidgetProps> = ({ src, showToolbar, inSmallWindow, allowDownload }) => {
  const pdfViewRef = useRef<HTMLDivElement | null>(null);
  const { turnOn: setFullscreenMode, turnOff: setWindowMode, isOn: fullscreenMode } = useSwitch();
  const fullScreenPluginInstance = fullScreenPlugin({
    getFullScreenTarget(): HTMLElement {
      // We want to return this even if this is undefined, as it might happen in some edge cases and package can handle it
      return pdfViewRef.current as never as HTMLElement;
    },

    // Don't show additional exit button
    renderExitFullScreenButton: () => <></>,
    onEnterFullScreen() {
      setFullscreenMode();
    },
    onExitFullScreen() {
      setWindowMode();
    },
  });

  const zoomPluginInstance = zoomPlugin();
  //document.fullscreenEnabled is not supported on iOS yet, the value can therefore be "undefined" too
  const isFullScreenAllowed: boolean = document.fullscreenEnabled || document.webkitFullscreenEnabled;

  const plugins = useMemo(
    () => createPlugins(fullScreenPluginInstance, zoomPluginInstance, allowDownload),
    [allowDownload, fullScreenPluginInstance, zoomPluginInstance]
  );
  if (!src) {
    return null;
  }
  return (
    <div className={classNames('pdf-preview', { 'pdf-preview--small': inSmallWindow })} ref={pdfViewRef}>
      {showToolbar && (
        <div
          className={classNames('pdf-preview__toolbar', {
            'pdf-preview__toolbar--small': inSmallWindow,
            'pdf-preview__toolbar--fullscreen': fullscreenMode,
          })}
        >
          <div className="pdf-preview__zoom">
            <zoomPluginInstance.ZoomOut>
              {(props: RenderZoomOutProps) => (
                <div className="pdf-preview__toolbar-element-margin">
                  <PdfPreviewButton
                    onClick={() => props.onClick()}
                    icon={<FontAwesomeIcon icon={solid('minus')} size="lg" />}
                  />
                </div>
              )}
            </zoomPluginInstance.ZoomOut>
            <zoomPluginInstance.CurrentScale>
              {(props: RenderCurrentScaleProps) => (
                <div className="pdf-preview__toolbar-element-margin">
                  <div className="pdf-preview__current-zoom-level">{`${Math.round(props.scale * 100)}%`}</div>
                </div>
              )}
            </zoomPluginInstance.CurrentScale>
            <zoomPluginInstance.ZoomIn>
              {(props: RenderZoomInProps) => (
                <PdfPreviewButton
                  onClick={() => props.onClick()}
                  icon={<FontAwesomeIcon icon={solid('plus')} size="lg" />}
                />
              )}
            </zoomPluginInstance.ZoomIn>
          </div>
          <div className="pdf-preview__controls">
            {allowDownload && (
              <div className="pdf-preview__toolbar-element-margin">
                <ExternalLinkWrapper href={src}>
                  <PdfPreviewButton icon={<FontAwesomeIcon icon={solid('file-download')} size="lg" />} />
                </ExternalLinkWrapper>
              </div>
            )}
            {isFullScreenAllowed && (
              <fullScreenPluginInstance.EnterFullScreen>
                {(fullscreenApi) => (
                  <PdfPreviewButton
                    onClick={() => {
                      fullscreenApi.onClick();
                    }}
                    icon={
                      <FontAwesomeIcon
                        icon={fullscreenMode ? solid('down-left-and-up-right-to-center') : solid('expand-alt')}
                        size="lg"
                        fixedWidth
                      />
                    }
                  />
                )}
              </fullScreenPluginInstance.EnterFullScreen>
            )}
          </div>
        </div>
      )}
      {
        // If you get worker version doesn't match pdfjs version, copy pdf.worker.js from /node_modules/pdfjs/legacy/build/pdf.worker.js
        // to the /public folder, this is necessary to do every time we do update of react-pdf-viewer package
      }
      <Worker workerUrl={`${PATH_CONFIG.PATH_TO_PUBLIC_FOLDER}pdf.worker.js`}>
        <Viewer fileUrl={src} plugins={plugins} defaultScale={SpecialZoomLevel.PageFit} />
      </Worker>
    </div>
  );
};
